import { Package } from "lucide-react";
import { PRODUCTS, CATEGORIES, formatRM } from "@/data/products";

export function ProductImage({ product, className = "" }) {
  if (product.image) {
    return <img src={product.image} alt={product.name} className={`w-full h-full object-contain ${className}`} />;
  }
  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center bg-[#efece6]"
      data-testid={`product-image-placeholder-${product.id}`}
    >
      <Package className="w-10 h-10 text-[#10233f]/30" strokeWidth={1.2} />
      <span className="text-xs font-bold tracking-widest uppercase text-[#0d7a72]">{product.category}</span>
      <span className="text-sm font-semibold text-[#10233f]/70 leading-snug line-clamp-3">{product.name}</span>
    </div>
  );
}

export default function Products({ filter, onFilter, onAdd, onView }) {
  const visible = PRODUCTS.filter((p) => p.visible);
  const list = filter === "All" ? visible : visible.filter((p) => p.category === filter);

  return (
    <section id="products" className="py-20 md:py-28 bg-[#f7f5f1]" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-[#0d7a72] text-sm font-bold tracking-[0.25em] uppercase mb-3">Catalogue</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#10233f] mb-4" data-testid="products-heading">
          Our Products
        </h2>
        <p className="text-base text-[#5a6b85] max-w-2xl mb-10">
          All prices are in Malaysian Ringgit (MYR). Add items to your cart and send your order via WhatsApp.
        </p>

        <div className="flex flex-wrap gap-2.5 mb-12" data-testid="product-filters">
          {["All", ...CATEGORIES].map((c) => (
            <button
              key={c}
              onClick={() => onFilter(c)}
              data-testid={`filter-${c === "All" ? "all" : c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${
                filter === c
                  ? "bg-[#0a1e3c] text-white"
                  : "bg-white text-[#10233f] border border-[#e0dad0] hover:border-[#0d7a72] hover:text-[#0d7a72]"
              }`}
            >
              {c === "All" ? "All Products" : c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="products-grid">
          {list.map((p) => (
            <article
              key={p.id}
              data-testid={`product-card-${p.id}`}
              className="bg-white rounded-2xl border border-[#e6e1d8] overflow-hidden card-lift flex flex-col"
            >
              <div className="aspect-[4/3] bg-white flex items-center justify-center p-5">
                <ProductImage product={p} />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <span className="text-xs font-bold tracking-widest uppercase text-[#0d7a72] mb-2" data-testid={`product-category-${p.id}`}>
                  {p.category}
                </span>
                <h3 className="font-bold text-[#10233f] leading-snug mb-3 flex-1" data-testid={`product-name-${p.id}`}>
                  {p.name}
                </h3>
                <p className="font-display text-xl font-semibold text-[#10233f] mb-5" data-testid={`product-price-${p.id}`}>
                  {formatRM(p.price)}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => onView(p)}
                    data-testid={`product-view-details-${p.id}`}
                    className="flex-1 px-4 py-2.5 rounded-full border border-[#10233f]/25 text-sm font-bold text-[#10233f] hover:border-[#0d7a72] hover:text-[#0d7a72] transition-colors"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => onAdd(p.id)}
                    data-testid={`product-add-to-cart-${p.id}`}
                    className="flex-1 px-4 py-2.5 rounded-full bg-[#0d7a72] text-sm font-bold text-white hover:bg-[#0a5f59] transition-colors"
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
